// App.js
import React, { Component, Suspense, lazy } from 'react';
import { HashRouter as Router, Switch, Route, Redirect, withRouter } from 'react-router-dom';

import './App.css';

import Main from './containers/Main/Main';
import PrivateRoute from './components/ProtectedRoute/PrivateRoute';
import RouteErrorBoundary from './components/RouteErrorBoundary';

import 'bootstrap/dist/css/bootstrap.min.css';
// react-super-responsive-table's stylesheet used to be imported here. Its only
// consumer (components/ReactiveTable) was dead code and is deleted, so the CSS
// and the dependency went with it.
import 'react-toastify/dist/ReactToastify.css';
// The 2026 redesign foundation (tokens, shared classes, reset scoped to
// `.ui-root`). Imported after Bootstrap/toastify; see src/ui/README.md.
import './ui/ui.css';
import { color as T, font as F } from './ui/tokens';
import AppShell from './ui/shell/AppShell';
import PublicShell from './ui/shell/PublicShell';
import LegacyPage from './ui/shell/LegacyPage';
import LegacyMount from './ui/shell/LegacyMount';
// One react-query client for the whole app (src/ui/data): the cache of the
// redesigned pages' reads survives route changes. Wiped on every session change.
import { QueryClientProvider } from '@tanstack/react-query';
import { appQueryClient } from './ui/data/queryClient';

import { persistor, Store } from './redux/Store';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { Provider } from 'react-redux';

import jwt_decode from 'jwt-decode';
import jwtAuthService from './services/jwtAuthService';

import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress } from '@mui/material';

import { OnboardingProvider } from './components/Onboarding/OnboardingProvider';

import { GoogleOAuthProvider } from '@react-oauth/google';

// ─── Route-level code splitting ────────────────────────────────────────────
// Every top-level screen is loaded with React.lazy so the first paint (the
// landing page / sign-in) no longer ships Trader_Form, the scanner, the
// settings hub and the Stripe pricing table with it. The route LIST and its
// ORDER in <Switch> below are unchanged — only the import style is.
//
// Rules if you add a route:
//   * lazy() the screen here, next to its siblings;
//   * the whole <Switch> is already inside ONE <Suspense>, so nothing else
//     needs a fallback;
//   * keep the guards (PrivateRoute / AuthRoute), <Main>, RouteErrorBoundary
//     and the providers EAGER — they render on every route, so splitting them
//     buys nothing and costs a spinner.
const Login = lazy(() => import('./components/Login/Login'));
const VerifyForm = lazy(() => import('./containers/Login/VerifyForm'));
const TempPassword = lazy(() => import('./containers/Login/tempPassword'));
const ResetPasswordForm = lazy(() => import('./containers/Login/ResetPasswordForm'));

const Prices = lazy(() => import('./containers/Payment/Prices'));
const SettingsLayout = lazy(() => import('./containers/Settings/SettingsLayout'));
const SharedView = lazy(() => import('./containers/Share/SharedView'));

const Portfolio = lazy(() => import('./containers/Portfolio/PortfolioOverview'));
// The redesigned Portfolio (unit P1); the old page stays at /profile/legacy/portfolio.
const PortfolioPage = lazy(() => import('./pages/Portfolio/Portfolio'));
const Trader_form = lazy(() => import('./containers/Trader_Form/Trader_Form'));
// Redesigned AutoTrader (unit T1); the old page stays at /profile/legacy/traderform.
const AutoTraderPage = lazy(() => import('./pages/AutoTrader/AutoTraderPage'));
const Scanner_form = lazy(() => import('./containers/Scanners/scanner_form'));
// Redesigned Scanners (unit S1); the old page stays at /profile/legacy/scanners.
const ScannersPage = lazy(() => import('./pages/Scanners'));
// Watchlist + scanner hit ledger (backend wave 3-C feature 13). Note the
// folder is `Scanner/` (singular) — `Scanners/` holds the scanner wizard.
const Watchlist = lazy(() => import('./containers/Scanner/Watchlist'));
// Market-data lake coverage + ticker lookup (edge platform phase 1).
const DataCoverage = lazy(() => import('./containers/Data/DataCoverage'));
const ResearchLayout = lazy(() => import('./containers/Research/ResearchLayout'));
// The redesigned Research section (unit R1); the old layout stays at /profile/legacy/research.
const ResearchPages = lazy(() => import('./pages/Research/ResearchPages'));
const About = lazy(() => import('./containers/About/About'));

const Landing = lazy(() => import('./containers/Landing_Page/Landing'));
const Terms = lazy(() => import('./components/Terms/Terms'));

// Redesigned public pages (unit L1, src/pages/Public). The old screens above
// stay importable for the /legacy/* and /profile/legacy/about safety-net routes.
const NewLanding = lazy(() => import('./pages/Public/Landing/Landing'));
const NewSignIn = lazy(() => import('./pages/Public/auth/SignIn'));
const NewSignUp = lazy(() => import('./pages/Public/auth/SignUp'));
const NewVerify = lazy(() => import('./pages/Public/auth/Verify'));
const NewForgotPassword = lazy(() => import('./pages/Public/auth/ForgotPassword'));
const NewResetPassword = lazy(() => import('./pages/Public/auth/ResetPassword'));
const NewAbout = lazy(() => import('./pages/Public/About/About'));
const Privacy = lazy(() => import('./components/Privacy/Privacy'));

// Dev-only gallery of every src/ui component in each state (the reference the
// page units and the evaluator compare against the mockups). The route is not
// registered in production builds.
const SHOW_UI_KIT = process.env.NODE_ENV !== 'production';
const UiKit = SHOW_UI_KIT ? lazy(() => import('./ui/UiKit')) : null;

// Shown while a route chunk is in flight. Deliberately minimal and on the app
// background colour so it reads as "still loading" rather than a broken paint.
const RouteFallback = () => (
  <Box
    role="status"
    aria-live="polite"
    aria-label="Loading"
    sx={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.default',
    }}
  >
    <CircularProgress size={28} />
  </Box>
);

// MUI theme on the redesign's tokens (src/ui/tokens.js — read from the
// approved mockups). New pages are built with src/ui components, not MUI
// visuals; this exists so any MUI still on screen (legacy pages, MUI used for
// behaviour) blends in: flat #3B6FA6 primary (no gradient, no lift on hover),
// IBM Plex Sans, #0A0D13 ground, #0E121A surfaces, 1 px #1A2130 lines.
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: T.primary, light: T.accent, dark: '#2F5A88', contrastText: T.white },
    secondary: { main: T.accent, contrastText: T.ground },
    success: { main: T.up },
    error: { main: T.down },
    warning: { main: T.watch },
    info: { main: T.accent },
    background: { default: T.ground, paper: T.surface },
    divider: T.line,
    text: { primary: T.text, secondary: T.mut, disabled: T.dim },
    action: { hover: T.hover, selected: T.selected },
  },
  shape: { borderRadius: 9 },
  // Stacking order shared with src/ui (see src/ui/README.md "Stacking order"):
  // tab bar 1100 < GettingStarted 1500 < ui Layer (dialog/drawer/sheet) 1600
  // < MUI drawer/modal/popover 1650-1700 (so an MUI Select or Autocomplete
  // opened inside a ui Drawer/Dialog shows on top) < ui Popover 1800
  // < MUI tooltip 1850 < ui toasts 1900 < onboarding Tour 2000.
  zIndex: { mobileStepper: 1000, fab: 1050, speedDial: 1050, appBar: 1100, drawer: 1650, modal: 1700, snackbar: 1750, tooltip: 1850 },
  typography: {
    fontFamily: F.sans,
    fontSize: 14,
    subtitle1: { fontSize: 15, fontWeight: 600, color: T.title, lineHeight: 1.3 },
    subtitle2: { fontSize: 13, fontWeight: 600, color: T.strong, lineHeight: 1.3 },
    body2: { fontSize: 13.5, lineHeight: 1.45 },
    caption: { fontSize: 12, color: T.label, lineHeight: 1.45 },
    h5: { fontSize: 24, fontWeight: 600, letterSpacing: '-0.3px', color: T.title },
    h6: { fontSize: 17, fontWeight: 600, color: T.title },
    button: { textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: { styleOverrides: { body: { backgroundColor: T.ground, color: T.text, fontVariantNumeric: 'tabular-nums' } } },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { backgroundColor: T.surface, borderColor: T.line, borderRadius: 14, boxShadow: 'none' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, fontSize: 13.5, borderRadius: 9, lineHeight: 1.3, boxShadow: 'none' },
        sizeSmall: { fontSize: 13, padding: '6px 12px' },
        containedPrimary: {
          color: T.white, background: T.primary, fontWeight: 600,
          '&:hover': { background: '#4479B2' },
          '&.Mui-disabled': { background: T.tag, color: T.dim },
        },
        outlined: { borderColor: T.lineAct, backgroundColor: T.tag, color: T.actText, '&:hover': { borderColor: T.lineSel, backgroundColor: T.tag } },
        outlinedPrimary: { color: T.actText, borderColor: T.lineAct, backgroundColor: T.tag },
        outlinedError: { color: T.down, borderColor: T.downLine, backgroundColor: 'transparent', '&:hover': { backgroundColor: T.downBg } },
        outlinedWarning: { color: T.watch, borderColor: T.warnLine, backgroundColor: 'transparent', '&:hover': { backgroundColor: T.watchBg } },
        outlinedSuccess: { color: T.up, borderColor: T.okLine, backgroundColor: T.upBg },
        text: { color: T.accent, '&:hover': { color: T.accentHover, backgroundColor: 'transparent' } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontFamily: F.sans, fontSize: 12, fontWeight: 500, borderRadius: 999, height: 24, backgroundColor: T.neutralTag, border: '1px solid transparent', color: T.pillText },
        sizeSmall: { height: 22 },
        label: { paddingLeft: 9, paddingRight: 9 },
        outlined: { backgroundColor: T.input, borderColor: T.lineInput, color: T.body2 },
        colorPrimary: { color: T.tagText, backgroundColor: T.tag, borderColor: 'transparent' },
        colorSuccess: { color: T.up, backgroundColor: T.upBg, borderColor: 'transparent' },
        colorError: { color: T.down, backgroundColor: T.downBg2, borderColor: 'transparent' },
        colorWarning: { color: T.watch, backgroundColor: T.watchBg, borderColor: 'transparent' },
        colorInfo: { color: T.accent, backgroundColor: T.infoBg, borderColor: 'transparent' },
        colorSecondary: { color: T.accent, backgroundColor: T.tint, borderColor: 'transparent' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, fontSize: 13.5, lineHeight: 1.5, padding: '6px 16px', alignItems: 'center' },
        standardInfo: { backgroundColor: T.infoBg, border: `1px solid ${T.infoLine}`, color: T.infoText, '& .MuiAlert-icon': { color: T.accent } },
        standardWarning: { backgroundColor: T.warnBg, border: `1px solid ${T.warnLine}`, color: T.warnText, '& .MuiAlert-icon': { color: T.watch } },
        standardError: { backgroundColor: T.errBg, border: `1px solid ${T.errLine}`, color: T.errText, '& .MuiAlert-icon': { color: T.down } },
        standardSuccess: { backgroundColor: T.okBg, border: `1px solid ${T.okLine}`, color: T.okText, '& .MuiAlert-icon': { color: T.up } },
      },
    },
    MuiAlertTitle: { styleOverrides: { root: { color: T.title, fontWeight: 600, fontSize: 13.5 } } },
    MuiTabs: { styleOverrides: { indicator: { backgroundColor: T.accent, height: 2 } } },
    MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 400, fontSize: 13.5, minHeight: 42, color: T.label, '&.Mui-selected': { color: T.title } } } },
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: 13.5, color: T.text, borderBottom: `1px solid ${T.lineRow}`, padding: '9px 12px' },
        head: { fontFamily: F.sans, fontSize: 12.5, color: T.faint, backgroundColor: 'transparent', fontWeight: 400 },
        stickyHeader: { backgroundColor: T.surface },
      },
    },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: T.raised, border: `1px solid ${T.lineAct}`, color: T.body2, fontSize: 12.5, lineHeight: 1.45, borderRadius: 8 }, arrow: { color: T.raised } } },
    MuiDivider: { styleOverrides: { root: { borderColor: T.lineRow } } },
    MuiLinearProgress: { styleOverrides: { root: { backgroundColor: T.line, borderRadius: 4 }, bar: { borderRadius: 4 } } },
    MuiFormHelperText: { styleOverrides: { root: { fontSize: 12, color: T.faint, marginLeft: 0 } } },
    MuiCheckbox: { styleOverrides: { root: { color: T.lineCheck, '&.Mui-checked': { color: T.primary } } } },
    MuiSwitch: { styleOverrides: { root: { '& .Mui-checked': { color: T.white }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: T.primary, opacity: 1 } }, track: { backgroundColor: T.lineMuted, opacity: 1 } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: T.input, borderRadius: 9,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: T.lineInput },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.lineAct },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.primary, borderWidth: 1 },
        },
        input: { color: T.text },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: T.label, '&.Mui-focused': { color: T.accent } } } },
    MuiSlider: { styleOverrides: { root: { color: T.primary } } },
    MuiPopover: { styleOverrides: { paper: { backgroundColor: T.raised, border: `1px solid ${T.lineAct}`, borderRadius: 12, boxShadow: '0 12px 30px rgba(0,0,0,.4)' } } },
    MuiMenuItem: { styleOverrides: { root: { fontSize: 13.5, color: T.body2, '&:hover': { backgroundColor: T.hover, color: T.title }, '&.Mui-selected': { backgroundColor: T.selected } } } },
    MuiDialog: { styleOverrides: { paper: { backgroundColor: T.raised, border: `1px solid ${T.lineAct}`, borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,.5)' } } },
  },
});

const CLOCK_SKEW_MS = 30 * 1000;

// Helper: check if token is expired (with grace period for clock skew)
const isTokenExpired = () => {
  const token = localStorage.getItem('token');
  if (!token) return true;
  try {
    const decoded = jwt_decode(token);
    if (!decoded.exp) return false;
    return decoded.exp * 1000 < Date.now() - CLOCK_SKEW_MS;
  } catch (e) {
    return true;
  }
};

const isAuthenticated = () => {
  return localStorage.getItem('authenticated') === 'true' && !isTokenExpired();
};

// Debounced activity listener — refresh at most once every 30s
let lastRefresh = 0;
const REFRESH_THROTTLE_MS = 30 * 1000;

const noteActivity = () => {
  const now = Date.now();
  if (now - lastRefresh > REFRESH_THROTTLE_MS) {
    lastRefresh = now;
    jwtAuthService.refreshSession();
  }
};
const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel'];
activityEvents.forEach(event => {
  window.addEventListener(event, noteActivity, { passive: true });
});
// A visible tab counts as activity: watching a running test, the scanner feed
// or the portfolio without touching anything must not sign the user out. A
// hidden tab (or a closed laptop) still times out after SESSION_TIMEOUT.
const VISIBLE_TAB_TICK_MS = 5 * 60 * 1000;
setInterval(() => {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') noteActivity();
}, VISIBLE_TAB_TICK_MS);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') noteActivity();
});

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// SessionWatcher: forces redirect to /sign-in when session expires
// SessionWatcher: forces redirect to /sign-in when session expires
class SessionWatcherInner extends Component {
  constructor(props) {
    super(props);
    this.state = { expiredAt: 0 };
  }

  componentDidMount() {
    this.interval = setInterval(this.checkSession, 15000);
    // Don't run checkSession immediately on mount — give the app a chance
    // to hydrate redux-persist and finish login flows
    this.mountTime = Date.now();

    window.addEventListener('focus', this.checkSession);
    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('storage', this.handleStorageChange);
    window.addEventListener('session-expired', this.handleExpiredEvent);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    window.removeEventListener('focus', this.checkSession);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('storage', this.handleStorageChange);
    window.removeEventListener('session-expired', this.handleExpiredEvent);
  }

  componentDidUpdate(_, prevState) {
    if (this.state.expiredAt && this.state.expiredAt !== prevState.expiredAt) {
      setTimeout(() => this.setState({ expiredAt: 0 }), 0);
    }
  }

  handleVisibility = () => {
    if (document.visibilityState === 'visible') this.checkSession();
  };

  handleStorageChange = (e) => {
    // Only trigger if the token is explicitly removed by another tab
    if (e.key === 'token' && e.oldValue && !e.newValue) {
      this.triggerExpiry();
    }
  };

  handleExpiredEvent = () => {
    this.triggerExpiry();
  };

  checkSession = () => {
    // Grace period: don't fire during the first 5 seconds after mount
    // to let login/hydration finish
    if (Date.now() - this.mountTime < 5000) return;

    const isAuth = localStorage.getItem('authenticated') === 'true';
    if (!isAuth) return;

    // ONLY log out on hard signals:
    // 1. The JWT itself has actually expired (server-issued exp claim)
    // We do NOT check isSessionActive() here because session_start may not
    // be set if the login flow doesn't use jwtAuthService.setSession()
    if (isTokenExpired()) {
      jwtAuthService.logout();
      this.triggerExpiry();
    }
  };

  triggerExpiry = () => {
    this.setState({ expiredAt: Date.now() });
  };

  render() {
    const { location } = this.props;
    const onProtectedRoute = location.pathname.startsWith('/profile');
    if (this.state.expiredAt && onProtectedRoute) {
      return <Redirect to="/sign-in" />;
    }
    return null;
  }
}

const SessionWatcher = withRouter(SessionWatcherInner);

const AuthRoute = ({ component: Component, ...props }) => (
  <Route
    {...props}
    render={(routeProps) => {
      // If they have an expired token sitting around, clean it up
      if (localStorage.getItem('authenticated') === 'true' && isTokenExpired()) {
        jwtAuthService.logout();
      }

      return isAuthenticated() ? (
        <Redirect to="/profile/portfolio" />
      ) : (
        <Component {...routeProps} />
      );
    }}
  />
);

// The app shell around /profile/*, only for a live session: a signed-out
// visitor is bounced to /sign-in by PrivateRoute and should not see the
// signed-in header flash first.
const ProfileChrome = ({ children }) => (isAuthenticated() ? <AppShell>{children}</AppShell> : children);

class App extends Component {
  render() {
    return (
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <QueryClientProvider client={appQueryClient}>
        <Provider store={Store}>
          <PersistGate persistor={persistor}>
            <Router basename="/">
              <OnboardingProvider>
              <SessionWatcher />

              {/* ONE Suspense boundary around the whole route table — every
                  screen below is React.lazy, and a per-route boundary would
                  flash a separate spinner for the nested /profile switch. */}
              <Suspense fallback={<RouteFallback />}>
              <Switch>
                {/* The landing page; the legacy public paths render it too and
                    /features, /about, /contact scroll to #product, #why, #faq. */}
                <AuthRoute exact path="/" component={NewLanding} />
                <AuthRoute exact path="/features" component={NewLanding} />
                <AuthRoute exact path="/about" component={NewLanding} />
                <AuthRoute exact path="/services" component={NewLanding} />
                <AuthRoute exact path="/team" component={NewLanding} />
                <AuthRoute exact path="/contact" component={NewLanding} />

                <AuthRoute exact path="/sign-in" component={NewSignIn} />
                <AuthRoute exact path="/verify" component={NewVerify} />
                <AuthRoute exact path="/sign-up" component={NewSignUp} />
                {/* #/changePassword was an AuthRoute, i.e. reachable only while
                    LOGGED OUT, and it posted {email, oldPassword, password} to
                    the unauthenticated POST /api/changeOfPassword. The
                    logged-in form now lives in Settings → Account (wave 3 D1),
                    which sends {oldPassword, newPassword} and takes the
                    identity from the JWT. The screen is deleted rather than
                    kept in two places; the path redirects so old bookmarks and
                    emails still land somewhere sensible. A logged-OUT visitor
                    falls through PrivateRoute to #/sign-in, which is correct —
                    changing a password you know requires signing in, and the
                    "I don't know it" case is #/tempPassword below. */}
                <Route exact path="/changePassword">
                  <Redirect to="/profile/settings/account" />
                </Route>
                <AuthRoute exact path="/tempPassword" component={NewForgotPassword} />

                {/* Landing page for the emailed password-reset link
                    (crypto-backend-api services/email.js builds
                    `${DOMAIN}/#/resetPassword?token=…`).

                    Deliberately a plain <Route>, not an <AuthRoute>: AuthRoute
                    redirects anyone holding a valid session to
                    /profile/portfolio, which would silently swallow the link
                    for a user who is still signed in on this device (a very
                    common case — "I forgot my password" is usually typed on a
                    phone while the desktop is logged in). It must also render
                    for a signed-OUT user, which it does: nothing here reads the
                    session, and Toolbar/Footer are only mounted inside <Main>
                    under /profile, so neither is on this route. */}
                <Route exact path="/resetPassword" component={NewResetPassword} />

                {/* Public read-only share page (crypto-backend-api feature 12).
                    A plain <Route> for the same reason /resetPassword is one,
                    plus a stronger one: the recipient of a share link is
                    usually not a user of this app at all, so PrivateRoute would
                    bounce them to #/sign-in and AuthRoute would bounce a
                    signed-in viewer to /profile/portfolio. Nothing on the page
                    reads the session, and services/http.js strips the
                    Authorization header from GET /api/share/:token so a
                    signed-in viewer never leaks their JWT to it. */}
                <Route path="/share/:token" component={SharedView} />
                {/* A run published to the OOS leaderboard (phase 5 item 5):
                    same public page, loaded from GET /api/leaderboard/:id
                    (also JWT-stripped in services/http.js). */}
                <Route path="/published/:id" component={SharedView} />

                {/* Terms, Privacy and About are ALSO public (DECISIONS A9): the
                    redesigned landing / auth footers link here, and a signed-out
                    visitor must not bounce to sign-in. Plain <Route>s (not
                    AuthRoute) so a signed-in visitor can read them too.
                    `/about` is already the landing's About anchor, hence
                    `/about-app`. The /profile/* versions below stay. */}
                <Route exact path="/terms">
                  <PublicShell><Terms /></PublicShell>
                </Route>
                <Route exact path="/privacy">
                  <PublicShell><Privacy /></PublicShell>
                </Route>
                <Route exact path="/about-app">
                  <PublicShell legacy={false}><NewAbout /></PublicShell>
                </Route>

                {/* Safety net (BUILD-GUIDE rule 5) for the public screens the
                    redesign replaced: the old landing page and the old tabbed
                    sign-in card (its Sign Up tab links to the new /sign-up) and
                    the old verify / forgot / reset forms. */}
                <Route exact path="/legacy/landing" component={Landing} />
                <Route exact path="/legacy/sign-in" component={Login} />
                <Route exact path="/legacy/verify" component={VerifyForm} />
                <Route exact path="/legacy/tempPassword" component={TempPassword} />
                <Route exact path="/legacy/resetPassword" component={ResetPasswordForm} />

                {/* Safety net (BUILD-GUIDE rule 5): the CURRENT pages, exactly
                    as they are today — no new shell, their own Toolbar/Nav —
                    until the user signs off the redesign. LegacyMount maps
                    each page's hard-coded paths (e.g. ResearchLayout's nested
                    "/profile/research/…" routes) onto /profile/legacy/…, so
                    they stay navigable after the new pages take the old URLs. */}
                <Route path="/profile/legacy">
                  <Main>
                    <RouteErrorBoundary>
                    <Switch>
                      <PrivateRoute path="/profile/legacy/portfolio">
                        <LegacyMount inner="/profile/portfolio" outer="/profile/legacy/portfolio"><Portfolio /></LegacyMount>
                      </PrivateRoute>
                      <PrivateRoute path="/profile/legacy/traderform">
                        <LegacyMount inner="/profile/traderform" outer="/profile/legacy/traderform"><Trader_form /></LegacyMount>
                      </PrivateRoute>
                      <PrivateRoute path="/profile/legacy/scanners">
                        <LegacyMount inner="/profile/scanners" outer="/profile/legacy/scanners"><Scanner_form /></LegacyMount>
                      </PrivateRoute>
                      <PrivateRoute path="/profile/legacy/research">
                        <LegacyMount inner="/profile/research" outer="/profile/legacy/research"><ResearchLayout /></LegacyMount>
                      </PrivateRoute>
                      <PrivateRoute path="/profile/legacy/about">
                        <About />
                      </PrivateRoute>
                      <Redirect to="/profile/portfolio" />
                    </Switch>
                    </RouteErrorBoundary>
                  </Main>
                </Route>

                {/* Every signed-in page renders inside the redesign's AppShell
                    (src/ui/shell): header / phone tab bar, user menu, halt chip,
                    global risk:blocked toast. Pages not yet rebuilt are wrapped
                    in <LegacyPage>, which keeps their current look; a page unit
                    replaces `<LegacyPage><Old /></LegacyPage>` with the new page.
                    The inner <Suspense> keeps the shell on screen while a page
                    chunk loads. */}
                <Route path="/profile">
                  <Main>
                    <ProfileChrome>
                    <RouteErrorBoundary>
                    <Suspense fallback={<RouteFallback />}>
                    <Switch>
                      <PrivateRoute path="/profile/portfolio">
                        <PortfolioPage />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/traderform">
                        <AutoTraderPage />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/scanners">
                        <ScannersPage />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/watchlist">
                        <LegacyPage><Watchlist /></LegacyPage>
                      </PrivateRoute>
                      <PrivateRoute path="/profile/data">
                        <LegacyPage><DataCoverage /></LegacyPage>
                      </PrivateRoute>
                      <PrivateRoute path="/profile/research">
                        <ResearchPages />
                      </PrivateRoute>
                      {/* /profile/orderhistory removed: the legacy Order_history
                          screen POSTs a raw Binance API key + secret to
                          /api/trade in cleartext form state, and its autotrade
                          endpoints identify the caller by localStorage email
                          rather than the JWT. Superseded by /profile/portfolio
                          and the exchange-key screens. */}
                      <PrivateRoute path="/profile/about">
                        <NewAbout />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/terms">
                        <LegacyPage><Terms /></LegacyPage>
                      </PrivateRoute>
                      <PrivateRoute path="/profile/privacy">
                        <LegacyPage><Privacy /></LegacyPage>
                      </PrivateRoute>

                      {/* Settings hub: /profile/settings/{account,tokens,audit,exports}.
                          SettingsLayout owns its own nested <Switch>, so this
                          route must NOT be `exact`. */}
                      <PrivateRoute path="/profile/settings">
                        <LegacyPage><SettingsLayout /></LegacyPage>
                      </PrivateRoute>

                      {/* Wave 1 shipped /profile/account and linked it from the
                          Toolbar and PortfolioOverview's own header. It is now
                          the Account tab of the settings hub; the old path is
                          kept working so those links and any bookmarks survive. */}
                      <Route exact path="/profile/account">
                        <Redirect to="/profile/settings/account" />
                      </Route>

                      {/* Stripe's <Elements> provider used to wrap this route
                          from OUTSIDE, as a direct child of the <Switch>. A
                          react-router v5 <Switch> renders the FIRST child whose
                          `path` matches and treats a child with NO `path` as a
                          match for everything — so <Elements> was a catch-all:
                          it swallowed every URL under /profile that no earlier
                          route had claimed (e.g. a typo'd path rendered the
                          pricing table), and any route declared after it was
                          unreachable. The provider now sits INSIDE the route,
                          wrapping only the screen that needs it, so <Switch>
                          sees a normal path-bearing child.

                          Anything added below this line is reachable. */}
                      <PrivateRoute path="/profile/subscription">
                        <Elements stripe={stripePromise}>
                          <LegacyPage><Prices /></LegacyPage>
                        </Elements>
                      </PrivateRoute>

                      {/* Dev-only component gallery (never in a production build). */}
                      {SHOW_UI_KIT && (
                        <PrivateRoute path="/profile/ui-kit">
                          <UiKit />
                        </PrivateRoute>
                      )}

                      {/* ADD NEW /profile ROUTES ABOVE THIS LINE.
                          With the <Elements> catch-all gone, an unrecognised
                          /profile/* URL matched nothing at all and rendered a
                          blank page inside the chrome. This last-child
                          <Redirect> (no `from`, so it matches anything that got
                          this far) sends it to the portfolio instead. */}
                      <Redirect to="/profile/portfolio" />
                    </Switch>
                    </Suspense>
                    </RouteErrorBoundary>
                    </ProfileChrome>
                  </Main>
                </Route>
              </Switch>
              </Suspense>
              </OnboardingProvider>
            </Router>
          </PersistGate>
        </Provider>
        </QueryClientProvider>
      </ThemeProvider>
      </GoogleOAuthProvider>
    );
  }
}

export default App;